import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import sortBy from "lodash/sortBy";

import { HOME_FOLDER_ID, HOME_FOLDER_LABEL, HOME_FOLDER_OPTION } from "fond/constants";
import { AccountBase } from "fond/types";
import { Folder, HomeFolder } from "fond/types/folder";
import { required } from "fond/utils/validation";

import Autocomplete, { AutocompleteProps } from "./Autocomplete";

type FolderPickerFieldProps = {
  /**
   * The FinalForm field name for the selected folder ID
   */
  name: string;
  /**
   * The list of folders available to this user.
   * This list can be different depending on the context in which this field is shown (e.g. moving a project vs. moving a folder)
   */
  folders: Folder[];
  /**
   * The account of the resource we're manipulating.
   * For instance for moving an entity, it will be the entity's account, for creating an entity, it will be the user's current account.
   */
  account: AccountBase;
  /**
   * Whether the field is shown in a more compact way
   */
  compact?: boolean;
  /**
   * A passed through change handler for when the selected folder changes.
   */
  onChange?: AutocompleteProps<FolderOption>["onChange"];
};

type FolderOption = Folder | HomeFolder;

export const FolderPickerField: React.FC<FolderPickerFieldProps> = ({
  name,
  folders,
  account,
  compact = false,
  onChange,
}: FolderPickerFieldProps) => {
  // folderOptions adds home route that allows user to move a project back to home screen
  // It sets the ID to "home" rather than null because TS will only allow for a string
  const folderOptions: FolderOption[] = useMemo(() => {
    return [HOME_FOLDER_OPTION, ...sortBy(folders, ["Path", "Name"])];
  }, [folders]);

  return (
    <Autocomplete<FolderOption>
      name={name}
      required
      label="Folder"
      fullWidth
      options={folderOptions}
      getOptionLabel={(option) => option.Name}
      getOptionValue={(option): FolderOption["ID"] => option?.ID || HOME_FOLDER_ID}
      onChange={onChange}
      renderOption={(props, option: FolderOption) => (
        <li {...props} key={option.ID}>
          <Box data-testid="move-folder-item" display="flex" flexDirection="column">
            <Typography>{option.Name}</Typography>
            <Typography variant="caption">
              {`${account.Name} > ${HOME_FOLDER_LABEL} ${option.Path.length ? " > " : ""} ${option.Path.join(" > ")}`}
            </Typography>
          </Box>
        </li>
      )}
      textFieldProps={{ size: compact ? "small" : "medium" }}
      filterOptions={(options, state) => options.filter((option) => option.Name.toLowerCase().includes(state.inputValue.toLowerCase()))}
      validate={required}
    />
  );
};
